import React from 'react';

import { Button } from './Button';

export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
  next_page?: number | null;
  prev_page?: number | null;
}

export interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  pagination,
  onPageChange,
  className = '',
}) => {
  const { page: currentPage, per_page: itemsPerPage, total: totalItems, pages: totalPages, has_next, has_prev } = pagination;
  
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="text-sm text-gray-700">
        {totalItems === 0 ? (
          'No results found'
        ) : (
          `Showing ${startItem} to ${endItem} of ${totalItems} results`
        )}
      </div>

      <div className="flex items-center space-x-1">
        {/* Previous Button */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!has_prev}
          className="px-3 py-2"
        >
          Previous
        </Button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {totalPages === 0 ? (
            <Button
              variant="primary"
              size="sm"
              disabled
              className="px-3 py-2 min-w-[40px]"
            >
              1
            </Button>
          ) : (
            getVisiblePages().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-3 py-2 text-sm text-gray-500">...</span>
                ) : (
                  <Button
                    variant={currentPage === page ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => onPageChange(page as number)}
                    className="px-3 py-2 min-w-[40px]"
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))
          )}
        </div>

        {/* Next Button */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!has_next}
          className="px-3 py-2"
        >
          Next
        </Button>
      </div>
    </div>
  );
};
